// src/utils/soundUtils.js - Sound utility functions

import { useCallback, useRef } from 'react';

/**
 * Simple sound player function
 * @param {string} soundPath - Path to the sound file (e.g., '/sfx/correct.mp3')
 * @param {number} volume - Volume level (0 to 1)
 */
export const playSound = (soundPath, volume = 0.5) => {
  try {
    const audio = new Audio(soundPath);
    audio.volume = Math.max(0, Math.min(1, volume)); // Clamp volume between 0 and 1
    audio.play().catch(e => {
      console.warn("Could not play sound:", e.message);
    });
  } catch (error) {
    console.error("Error creating audio:", error);
  }
};

/**
 * Hook for sound effects with better performance
 * @param {string} soundUrl - URL to the sound file
 * @param {number} volume - Volume level (0 to 1)
 * @param {boolean} preload - Whether to preload the sound
 */
export const useSound = (soundUrl, volume = 0.5, preload = false) => {
  const audioRef = useRef(null);

  // Initialize audio object
  if (!audioRef.current && soundUrl) {
    audioRef.current = new Audio(soundUrl);
    audioRef.current.volume = Math.max(0, Math.min(1, volume));

    if (preload) {
      audioRef.current.preload = 'auto';
      audioRef.current.load();
    }
  }

  const playSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0; // Reset to beginning
      audioRef.current.play().catch(e => {
        console.warn("Could not play sound:", e.message);
      });
    }
  }, []);

  const stopSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const setVolume = useCallback((newVolume) => {
    if (audioRef.current) {
      audioRef.current.volume = Math.max(0, Math.min(1, newVolume));
    }
  }, []);

  return { playSound, stopSound, setVolume };
};

/**
 * Pre-defined sound effects for the app
 */
export const SOUNDS = {
  CORRECT: '/sfx/correct.mp3',
  INCORRECT: '/sfx/incorrect.mp3',
  SUCCESS: '/sfx/success.mp3',
  CLICK: '/sfx/click.mp3',
  FLIP: '/sfx/flip.mp3',
  GRAMMAR_CORRECT: '/sfx/grammar_correct.mp3',
  NOTIFICATION: '/sfx/notification.mp3',
  LEVEL_UP: '/sfx/level-up.mp3',
  ACHIEVEMENT: '/sfx/achievement.mp3'
};

/**
 * Sound manager for global sound control
 */
class SoundManager {
  constructor() {
    this.enabled = true;
    this.volume = 0.5;
    this.sounds = new Map();
  }

  /**
   * Enable or disable all sounds
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('soundEnabled', enabled.toString());
    }
  }

  /**
   * Set global volume
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('soundVolume', this.volume.toString());
    }
  }

  /**
   * Load settings from localStorage
   */
  loadSettings() {
    if (typeof localStorage !== 'undefined') {
      const savedEnabled = localStorage.getItem('soundEnabled');
      const savedVolume = localStorage.getItem('soundVolume');

      if (savedEnabled !== null) {
        this.enabled = savedEnabled === 'true';
      }

      if (savedVolume !== null) {
        this.volume = parseFloat(savedVolume) || 0.5;
      }
    }
  }

  /**
   * Play a sound with global settings applied
   */
  play(soundPath, volume = null) {
    if (!this.enabled) return;

    const finalVolume = volume !== null ? volume : this.volume;
    playSound(soundPath, finalVolume);
  }

  /**
   * Play a predefined sound
   */
  playEffect(effectName) {
    const soundPath = SOUNDS[effectName.toUpperCase()];
    if (soundPath) {
      this.play(soundPath);
    } else {
      console.warn(`Sound effect not found: ${effectName}`);
    }
  }

  /**
   * Preload multiple sounds for better performance
   */
  preloadSounds(soundPaths) {
    soundPaths.forEach(soundPath => {
      if (!this.sounds.has(soundPath)) {
        try {
          const audio = new Audio(soundPath);
          audio.preload = 'auto';
          audio.load();
          this.sounds.set(soundPath, audio);
        } catch (error) {
          console.warn(`Failed to preload sound: ${soundPath}`, error);
        }
      }
    });
  }
}

// Create global sound manager instance
export const soundManager = new SoundManager();

// Initialize settings on load
if (typeof window !== 'undefined') {
  soundManager.loadSettings();
}

/**
 * Hook for using the sound manager in React components
 */
export const useSoundManager = () => {
  return {
    play: soundManager.play.bind(soundManager),
    playEffect: soundManager.playEffect.bind(soundManager),
    setEnabled: soundManager.setEnabled.bind(soundManager),
    setVolume: soundManager.setVolume.bind(soundManager),
    enabled: soundManager.enabled,
    volume: soundManager.volume
  };
};

/**
 * Convenience hooks for common sounds
 */
export const useCorrectSound = () => {
  const { playSound } = useSound(SOUNDS.CORRECT, 0.6, true);
  return playSound;
};

export const useIncorrectSound = () => {
  const { playSound } = useSound(SOUNDS.INCORRECT, 0.6, true);
  return playSound;
};

export const useSuccessSound = () => {
  const { playSound } = useSound(SOUNDS.SUCCESS, 0.7, true);
  return playSound;
};

export const useClickSound = () => {
  const { playSound } = useSound(SOUNDS.CLICK, 0.3, true);
  return playSound;
};

// Export default for convenience
export default {
  playSound,
  useSound,
  SOUNDS,
  soundManager,
  useSoundManager,
  useCorrectSound,
  useIncorrectSound,
  useSuccessSound,
  useClickSound
};